import { AzureTeam, AzureTeamMember } from "../../types/azureTeams";

export enum SettingType {
  INTEGRATION = "Integrations",
  LLM = "VectorSettings",
  ORG_SETTINGS = "OrgSettings",
  SKILLS = "OrgSkills",
  HOLIDAYS = "Schedules",
  NOTIFICATIONS = "Notifications",
  PERSONALIZATION = "Personalization"
}

export interface Preference {
  pref_id: number;
  preference_text: string;
}

export enum LLM_MODEL_TYPES {
  GEMINI_3_1_PRO = "gemini-3.1-pro-preview",
  GEMINI_2_5_FLASH = "gemini-2.5-flash",
  GEMINI_3_0_FLASH = "gemini-3-flash-preview",
  GEMINI_3_0_PRO = "gemini-3-pro-preview"
}

export interface LLMSettings {
  provider: string;
  api_key: string;
  model: string;
}

export interface LlmSettingsResponse {
  settings?: LLMSettings | null;
}

export interface SettingsState {
  activeTab: SettingType;
  isLLMSettingsLoading: boolean;
  llmSettings: LLMSettings | null;
  // When true, the Personalization page should auto-open the "Saved user memory" manage modal
  openUserMemoryManageModal: boolean;
  // Slack Notifications - flattened
  slackTargetsLoading: boolean;
  slackTargetsError: string | null;
  slackChannels: SlackChannel[];
  slackUsers: SlackUser[];
  slackAvailableNotificationsLoading: boolean;
  slackAvailableNotifications: AvailableNotification[];
  slackTriggersLoading: boolean;
  slackTriggersError: string | null;
  slackTriggers: NotificationTriggerResponse[];

  // Slack Notifications Wizard - flattened
  slackWizardIsOpen: boolean;
  slackWizardMode: 'create' | 'edit';
  slackWizardStep: WizardStep;
  slackWizardSelectedNotificationKey: string | null;
  slackWizardSelectedEntityType: string | null;
  slackWizardSelectedEntities: string[];
  slackWizardSelectedEntitiesData: SelectedEntityData[];
  slackWizardSelectedFrequency: FrequencyType;
  slackWizardSelectedChannels: ChannelOption[];
  slackWizardSelectedTaskTriggerTypes: TaskTriggerType[];
  slackWizardSelectionMethod: 'active' | 'selective' | null;
  slackWizardPendingActiveSelection: { type: 'strategy_map' | 'work_unit'; limit: number } | null;
  slackWizardSprints: any[];
  slackWizardLoadingSprints: boolean;
  slackWizardHasFetchedSprints: boolean;
  slackWizardCronConfig: CronConfig;
  slackWizardWorkUnitTypeFilter: 'all' | 'roadmap' | 'project';
  slackWizardSprintIntegration: 'jira' | 'azure' | null;

  // Persisted sync flags
  slackUserSyncRunning: boolean;

  // Cached name maps for ID->name lookups
  projectIdNameMap?: Record<string, string>;
  roadmapIdNameMap?: Record<string, string>;
  strategyMapIdNameMap?: Record<string, string>;
}

export interface SkillsPayload {
  skill_cost: string,
  skill: string,
  description: string,
  id: number,
  cost_per_hour: number,
  billing_per_hour?: number,
  color_code: string
}

export enum IntegrationActiveType {
  AZURE = 'azure',
  JIRA = 'jira',
}

export interface TeamsChanges {
  teamsToAdd: AzureTeam[],
  teamsToRemove: AzureTeam[],
  membersToAdd: { team: AzureTeam; member: AzureTeamMember }[],
  membersToRemove: { team: AzureTeam; member: AzureTeamMember }[]
}

export interface SlackSettings {
  is_active: boolean;
  access_token: string | null;
  connection_status: boolean;
  team_id: string | null;
  team_name: string | null;
}

export interface SlackSettingsUpdateRequest {
  is_active: boolean;
}

export interface SlackConnectUrlResponse {
  oauth_url: string;
  message?: string;
}

export interface SyncSlackUsersResponse {
  message?: string;
}

export interface SyncSlackUsersStatusResponse {
  message: string,
  is_syncing: boolean
}

export interface SlackDisconnectResponse {
  message: string;
}

export interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
  is_archived: boolean;
  is_channel: boolean;
  is_group: boolean;
  is_im: boolean;
  num_members: number;
  purpose: string;
  topic: string;
}

export interface SlackUser {
  id: number;
  org_id: string;
  slack_user_id: string;
  team_id: string;
  name: string;
  email: string;
}

export interface SlackTargetsResponse {
  channels: SlackChannel[];
  users: SlackUser[];
}

// Slack Notifications (data/state separate from wizard UI)
export interface SlackNotificationsState {
  channels: SlackChannel[];
  users: SlackUser[];
  targetsLoading: boolean;
  targetsError: string | null;
  availableNotifications: AvailableNotification[];
  availableNotificationsLoading: boolean;
  triggers: NotificationTriggerResponse[]; // reusing backend response shape
  triggersLoading: boolean;
  triggersError: string | null;
}

// Slack Notifications Wizard Types
export type SlackRecipientType = 'channel' | 'user';

export interface ChannelOption {
  value: string;
  label: string;
  type?: SlackRecipientType;
}

export interface WizardOption {
  value: string;
  label: string;
  isActive?: boolean;
  type?: string;  // 'roadmap' or 'project' for work items
}

export type WizardStep = 'entity_type' | 'config';

export interface SlackNotificationsWizardState {
  isOpen: boolean;
  mode: 'create' | 'edit';
  step: WizardStep;
  selectedNotificationKey: string | null;
  selectedEntityType: string | null;
  selectedEntities: string[];
  selectedFrequency: FrequencyType;
  selectedChannels: ChannelOption[];
  selectedTaskTriggerTypes: TaskTriggerType[];
  selectionMethod: 'active' | 'selective' | null;
  pendingActiveSelection: { type: 'strategy_map' | 'work_unit'; limit: number } | null;
  sprints: any[];
  loadingSprints: boolean;
  hasFetchedSprints: boolean;
  cronConfig: CronConfig;
}

export type NotificationEntityType = 'work_unit' | 'strategy_map' | 'strategic_map' | 'sprint_report' | 'sprint' | 'task' | 'task_changes';

export type NotificationFrequency = 'immediate' | 'daily' | 'weekly' | 'biweekly' | 'monthly';

// Entity data object for selected work items
export interface SelectedEntityData {
  entity_id: string;
  type: string;  // 'roadmap' or 'project' - maps from backend 'type' field
}

export interface NotificationTriggerRequest {
  notification_id: number;
  trigger_id?: number;
  entity_type: NotificationEntityType;
  frequency: NotificationFrequency;
  send_to: NotificationTarget[];
  configuration: TriggerConfiguration[];
  status: 'enabled' | 'disabled';
}

export interface NotificationTarget {
  target_type: 'channel' | 'user';
  slack_id: string;
  user_id?: string;
  email?: string;
  label: string;
  metadata?: Record<string, any>;
}

export interface TriggerConfiguration {
  type?: 'active' | 'selective';
  entity_id?: string[];
  triggers?: string[];
  [key: string]: any;
}

export interface NotificationListItem {
  notification: AvailableNotification;
  label: string;
  description: string;
  sortOrder: number;
  isDisabled: boolean;
}

export interface NotificationTriggersListResponse {
  notification_triggers: NotificationTriggerResponse[];
}

export interface NotificationTriggerResponse {
  id: number;
  notification_key?: string;
  entity_type: string;
  frequency: string;
  send_to: NotificationTarget[];
  configuration: TriggerConfiguration[];
  notification_type_id: number;
  status: string;
  created_at?: string;
  updated_at?: string;
}

export interface AvailableNotificationsResponse {
  enabled?: boolean;
  notifications: AvailableNotification[];
}

export interface AvailableNotification {
  id: number;
  key: string;
  name: string;
  description: string;
  entity_type: string;
  is_recurring: boolean;
  delivery_scope?: string;
  default_frequency?: string;
  default_delivery_channels?: Array<{
    target_type: 'channel' | 'user';
    delivery_channel: string;
  }>;
  metadata?: {
    supports_frequencies?: string[];
    [key: string]: any;
  };
  created_at?: string;
  modified_at?: string;
}

export interface NotificationTrigger {
  id: string;
  entity_type: string;
  entityTypeLabel: string;
  entity_ids: string[];
  entity_names: string[];
  frequency: FrequencyType;
  send_to: string[];
  details: string;
  enabled: boolean;
  notificationKey: string;
  supportedFrequencies: FrequencyType[];
  project_id?: string;
  project_name?: string;
  team_ids?: string[];
  team_names?: string[];
  taskTriggerTypes?: TaskTriggerType[];
  cron?: CronConfig;
}

export type FrequencyType = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'immediate';
export type TaskTriggerType = 'assigned' | 'status_changed' | 'priority_updated' | 'due_date_changed';

// Cron configuration for scheduled notifications
export interface CronConfig {
  day: number;      // For daily: 0, For weekly: 0-6 (day of week), For monthly: 1-31 (day of month)
  hour: number;     // 0-23
  minute: number;   // 0-59
  timezone: string; // e.g., 'America/New_York', 'UTC', 'Asia/Kolkata'
}